import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import moment from "moment";
import { PageHeader } from "components/ui";
import { useFetch } from "hooks";
import { get, post, del } from "utils/api";
import { errorMessage, message } from "actions/message";
import "./style.scss";

// Plain (non-redux) API calls for this feature — the client prepends the API host
// and attaches auth headers. Paths omit the "api/" prefix.
const api = {
  list: () => get("system-users"),
  resolve: email => get("system-users/resolve", { email }),
  add: (category, email) => post(`system-users/${category}/members`, { email }),
  remove: id => del(`system-users/members/${id}`)
};

// Friendly section titles for the raw category keys returned by the API.
const CATEGORY_LABELS = {
  SuperAdmin: "Super Admins",
  NewCompanyAccess: "New Company Access",
  Synthpop: "Synthpop Requestor",
  ComplianceExpire: "Compliance Expiration Requestor",
  Default: "Default Requestor"
};

const labelFor = category =>
  CATEGORY_LABELS[category] || category.replace(/([a-z])([A-Z])/g, "$1 $2");

// Frontend overrides for the per-category description. When present these take
// precedence over the backend-supplied description; anything not listed falls
// back to view.description.
const CATEGORY_DESCRIPTIONS = {
  SuperAdmin:
    "Super Admins can edit times on the Team Dashboard — that is the only capability this grants today."
};

const descriptionFor = view =>
  CATEGORY_DESCRIPTIONS[view.category] || view.description;

const formatDate = value => (value ? moment(value).format("MM/DD/YYYY") : "—");

// The backend returns 400 with a plain string body (e.Message). Pull that out so
// the user sees the real reason (duplicate, unknown email, last super admin, etc.)
// instead of a generic failure.
const serverMessage = (error, fallback) => {
  const body = error && error.response ? error.response.body : undefined;
  if (typeof body === "string" && body.trim()) return body;
  const text = error && error.response ? error.response.text : undefined;
  if (typeof text === "string" && text.trim()) {
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === "string" && parsed.trim()) return parsed;
      if (parsed && parsed.Message) return parsed.Message;
    } catch (e) {
      return text;
    }
  }
  return fallback;
};

const MemberRow = ({ member, onRemove }) => (
  <tr>
    <td>{member.name || "—"}</td>
    <td>{member.email || "—"}</td>
    <td>{member.active ? "Yes" : "No"}</td>
    <td>{member.addedByName || "—"}</td>
    <td>{formatDate(member.dtCreated)}</td>
    <td>
      <span className="su-remove" onClick={() => onRemove(member)}>
        Remove
      </span>
    </td>
  </tr>
);

MemberRow.propTypes = {
  member: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired
};

const CategorySection = ({
  view,
  inputValue,
  pending,
  busy,
  onInputChange,
  onSubmit,
  onConfirm,
  onCancel,
  onRemove
}) => {
  const isRequestor = view.kind === "requestor";
  const members = view.members || [];
  const actionLabel = isRequestor ? "Set" : "Add";

  return (
    <section className="su-category">
      <h3>{labelFor(view.category)}</h3>
      <p className="su-description">{descriptionFor(view)}</p>

      {isRequestor ? (
        <div className="su-requestor-current">
          <strong>Current: </strong>
          {members.length ? (
            <span>
              {members[0].name || "—"} ({members[0].email || "—"})
              {!members[0].active && <em> — inactive</em>}
              <span className="su-remove" onClick={() => onRemove(members[0])}>
                Clear
              </span>
            </span>
          ) : (
            <span className="su-none">None set</span>
          )}
        </div>
      ) : (
        <table className="su-members">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Active</th>
              <th>Added by</th>
              <th>Added on</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.length ? (
              members.map(m => (
                <MemberRow key={m.id} member={m} onRemove={onRemove} />
              ))
            ) : (
              <tr>
                <td colSpan={6} className="su-none">
                  No members yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {pending ? (
        <div className="su-confirm">
          <span>
            {actionLabel} <strong>{pending.name}</strong> ({pending.email})?
          </span>
          <button
            type="button"
            className="su-btn su-btn-primary"
            disabled={busy}
            onClick={() => onConfirm(view.category)}
          >
            Confirm
          </button>
          <button
            type="button"
            className="su-btn"
            disabled={busy}
            onClick={() => onCancel(view.category)}
          >
            Cancel
          </button>
        </div>
      ) : (
        <form
          className="su-add"
          onSubmit={e => {
            e.preventDefault();
            onSubmit(view.category);
          }}
        >
          <input
            type="email"
            placeholder="user@example.com"
            value={inputValue || ""}
            disabled={busy}
            onChange={e => onInputChange(view.category, e.target.value)}
          />
          <button
            type="submit"
            className="su-btn su-btn-primary"
            disabled={busy || !inputValue}
          >
            {actionLabel}
          </button>
        </form>
      )}
    </section>
  );
};

CategorySection.propTypes = {
  view: PropTypes.object.isRequired,
  inputValue: PropTypes.string,
  pending: PropTypes.object,
  busy: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};

const SystemUsersAdmin = () => {
  const dispatch = useDispatch();
  const [inputs, setInputs] = React.useState({});
  const [pending, setPending] = React.useState({});
  const [busy, setBusy] = React.useState({});

  const {
    response: views,
    fetchData: loadSystemUsers,
    isFetching,
    hasFetched
  } = useFetch({
    apiFn: api.list,
    defaultValue: [],
    clearDataOnFetch: false, // keep the current lists visible while refetching
    onError: error =>
      dispatch(errorMessage(serverMessage(error, "Failed to load system users.")))
  });

  React.useEffect(() => {
    loadSystemUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBusyFor = (category, value) =>
    setBusy(prev => ({ ...prev, [category]: value }));

  const onInputChange = (category, value) =>
    setInputs(prev => ({ ...prev, [category]: value }));

  // Resolve the email first so the admin confirms the actual person before adding.
  const onSubmit = async category => {
    const email = (inputs[category] || "").trim();
    if (!email) return;
    try {
      setBusyFor(category, true);
      const resolved = await api.resolve(email);
      setPending(prev => ({ ...prev, [category]: resolved }));
    } catch (error) {
      dispatch(
        errorMessage(serverMessage(error, "No active user found for that email."))
      );
    } finally {
      setBusyFor(category, false);
    }
  };

  const onConfirm = async category => {
    const resolved = pending[category];
    if (!resolved) return;
    try {
      setBusyFor(category, true);
      await api.add(category, resolved.email);
      setPending(prev => ({ ...prev, [category]: null }));
      setInputs(prev => ({ ...prev, [category]: "" }));
      dispatch(message("User added."));
      await loadSystemUsers();
    } catch (error) {
      dispatch(errorMessage(serverMessage(error, "Failed to add user.")));
    } finally {
      setBusyFor(category, false);
    }
  };

  const onCancel = category =>
    setPending(prev => ({ ...prev, [category]: null }));

  const onRemove = async member => {
    if (
      !window.confirm(
        `Remove ${member.name || member.email} from this category?`
      )
    )
      return;
    try {
      setBusyFor(member.category, true);
      await api.remove(member.id);
      dispatch(message("User removed."));
      await loadSystemUsers();
    } catch (error) {
      dispatch(errorMessage(serverMessage(error, "Failed to remove user.")));
    } finally {
      setBusyFor(member.category, false);
    }
  };

  const categoryViews = views || [];

  return (
    <div className="system-users-admin">
      <PageHeader title="System Users" />
      {isFetching && !hasFetched ? (
        <div className="su-loading">Loading…</div>
      ) : (
        categoryViews.map(view => (
          <CategorySection
            key={view.category}
            view={view}
            inputValue={inputs[view.category]}
            pending={pending[view.category]}
            busy={!!busy[view.category]}
            onInputChange={onInputChange}
            onSubmit={onSubmit}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onRemove={m => onRemove({ ...m, category: view.category })}
          />
        ))
      )}
    </div>
  );
};

export default SystemUsersAdmin;
